"use client";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";
import { IBookTypeData } from "../apiservice/userService.type";
import { DownloadProgress } from "../utils/appconst";

interface BookContextType {
  books: IBookTypeData[];
  downloadedBooks: number[];
  downloadProgress: DownloadProgress[];
  addBook: (book: IBookTypeData) => void;
  removeBook: (bookId: number) => void;
  getBook: (bookId: number) => IBookTypeData | undefined;
  downloadBookFiles: (book: IBookTypeData) => Promise<void>;
  isBookDownloaded: (bookId: number) => boolean;
  getLocalFilePath: (
    bookId: number,
    fileName: string
  ) => Promise<string | null>;
  clearDownloadedBooks: () => void;
}

const BookContext = createContext<BookContextType | undefined>(undefined);

interface BookProviderProps {
  children: ReactNode;
}

export function BookProvider({ children }: BookProviderProps) {
  const [books, setBooks] = useState<IBookTypeData[]>([]);
  const [downloadedBooks, setDownloadedBooks] = useState<number[]>([]);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress[]>(
    []
  );

  const addBook = useCallback((book: IBookTypeData) => {
    alert("Book Downloading Check Download for progress");
    setBooks((prev) => {
      const exists = prev.find((b) => b.id === book.id);
      if (exists) {
        return prev.map((b) => (b.id === book.id ? book : b));
      }
      return [...prev, book];
    });
  }, []);

  const removeBook = useCallback((bookId: number) => {
    setBooks((prev) => prev.filter((book) => book.id !== bookId));
    setDownloadedBooks((prev) => prev.filter((id) => id !== bookId));
  }, []);

  const getBook = useCallback(
    (bookId: number) => {
      return books.find((book) => book.id === bookId);
    },
    [books]
  );

  const isBookDownloaded = useCallback(
    (bookId: number) => {
      return downloadedBooks.includes(bookId);
    },
    [downloadedBooks]
  );

  const updateDownloadProgress = useCallback((progress: DownloadProgress) => {
    setDownloadProgress((prev) => {
      const existing = prev.find(
        (p) => p.bookId === progress.bookId && p.fileName === progress.fileName
      );
      if (existing) {
        return prev.map((p) =>
          p.bookId === progress.bookId && p.fileName === progress.fileName
            ? progress
            : p
        );
      }
      return [...prev, progress];
    });
  }, []);

  const downloadFile = useCallback(
    async (url: string, fileName: string, bookId: number): Promise<string> => {
      // if (!Capacitor.isNativePlatform()) {
      //   throw new Error("File download is only available on native platforms");
      // }

      try {
        updateDownloadProgress({
          bookId,
          fileName,
          progress: 0,
          status: "downloading",
        });

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch ${fileName}: ${response.statusText}`
          );
        }

        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        let binaryString = "";
        for (let i = 0; i < uint8Array.length; i++) {
          binaryString += String.fromCharCode(uint8Array[i]);
        }
        const base64Data = btoa(binaryString);

        const bookDir = `books/${bookId}`;
        try {
          await Filesystem.mkdir({
            path: bookDir,
            directory: Directory.Documents,
            recursive: true,
          });
        } catch (error) {
          // Directory might already exist, ignore error
        }

        const filePath = `${bookDir}/${fileName}`;
        await Filesystem.writeFile({
          path: filePath,
          data: base64Data,
          directory: Directory.Documents,
        });

        updateDownloadProgress({
          bookId,
          fileName,
          progress: 100,
          status: "completed",
        });

        return filePath;
      } catch (error) {
        updateDownloadProgress({
          bookId,
          fileName,
          progress: 0,
          status: "error",
        });
        throw error;
      }
    },
    [updateDownloadProgress]
  );

  const downloadBookFiles = useCallback(
    async (book: IBookTypeData) => {
      // if (!Capacitor.isNativePlatform()) {
      //   throw new Error("File download is only available on native platforms");
      // }

      try {
        const filesToDownload: { url: string; fileName: string }[] = [];

        if (book.coverImageUrl) {
          const coverFileName = `cover_${book.id}.${
            book.coverImageUrl.split(".").pop() || "jpg"
          }`;
          filesToDownload.push({
            url: book.coverImageUrl,
            fileName: coverFileName,
          });
        }

        if (book.bookFileUrl) {
          const bookFileName = `book_${book.id}.${
            book.bookFileUrl.split(".").pop() || "pdf"
          }`;
          filesToDownload.push({
            url: book.bookFileUrl,
            fileName: bookFileName,
          });
        }

        if (book.mainAudio?.fileUrl) {
          const mainAudioFileName = `main_audio_${book.id}.${
            book.mainAudio.fileUrl.split(".").pop() || "mp3"
          }`;
          filesToDownload.push({
            url: book.mainAudio.fileUrl,
            fileName: mainAudioFileName,
          });
        }

        book.audios?.forEach((audio, index) => {
          if (audio.fileUrl) {
            const audioFileName = `audio_${book.id}_${index}.${
              audio.fileUrl.split(".").pop() || "mp3"
            }`;
            filesToDownload.push({
              url: audio.fileUrl,
              fileName: audioFileName,
            });
          }
        });

        const downloadPromises = filesToDownload.map(({ url, fileName }) =>
          downloadFile(url, fileName, book.id)
        );
        await Promise.all(downloadPromises);

        const metadataPath = `books/${book.id}/metadata.json`;
        await Filesystem.writeFile({
          path: metadataPath,
          data: JSON.stringify(book, null, 2),
          directory: Directory.Documents,
          encoding: Encoding.UTF8,
        });

        setDownloadedBooks((prev) => {
          if (!prev.includes(book.id)) {
            return [...prev, book.id];
          }
          return prev;
        });

        setDownloadProgress((prev) => prev.filter((p) => p.bookId !== book.id));
      } catch (error) {
        console.error("Error downloading book files:", error);
        throw error;
      }
    },
    [downloadFile]
  );

  const getLocalFilePath = useCallback(
    async (bookId: number, fileName: string): Promise<string | null> => {
      // if (!Capacitor.isNativePlatform()) {
      //   return null;
      // }

      try {
        const filePath = `books/${bookId}/${fileName}`;
        const fileExists = await Filesystem.stat({
          path: filePath,
          directory: Directory.Documents,
        });

        if (fileExists) {
          const fileUri = await Filesystem.getUri({
            path: filePath,
            directory: Directory.Documents,
          });
          return fileUri.uri;
        }
      } catch (error) {
        console.error("Error getting local file path:", error);
      }

      return null;
    },
    []
  );

  const clearDownloadedBooks = useCallback(async () => {
    // if (!Capacitor.isNativePlatform()) {
    //   return;
    // }

    try {
      for (const bookId of downloadedBooks) {
        try {
          await Filesystem.rmdir({
            path: `books/${bookId}`,
            directory: Directory.Documents,
            recursive: true,
          });
        } catch (error) {
          console.error(`Error removing book ${bookId}:`, error);
        }
      }

      setDownloadedBooks([]);
      setDownloadProgress([]);
    } catch (error) {
      console.error("Error clearing downloaded books:", error);
    }
  }, [downloadedBooks]);

  const value: BookContextType = {
    books,
    downloadedBooks,
    downloadProgress,
    addBook,
    removeBook,
    getBook,
    downloadBookFiles,
    isBookDownloaded,
    getLocalFilePath,
    clearDownloadedBooks,
  };

  return <BookContext.Provider value={value}>{children}</BookContext.Provider>;
}

export function useBook() {
  const context = useContext(BookContext);
  if (context === undefined) {
    throw new Error("useBook must be used within a BookProvider");
  }
  return context;
}
